import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  PlusIcon,
  TrashIcon,
  ArrowPathIcon,
  LinkIcon,
} from "@heroicons/react/24/outline";
import { PMDialog } from "../../../utils/PMDialog";
import ModernProductSelectorV2 from "../../../utils/Selector/ModernProductSelectorV2";
import { fetchProduct } from "../../../../api/product";
import {
  applyCatalogPricesToList,
  applyCatalogPricesToOption,
  createManualProductListOption,
  emptyProductList,
  isLinkedProductListOption,
  linkProductListOptionToProduct,
  normalizeProductList,
  normalizeProductListOption,
  PRODUCT_LIST_MODES,
} from "../utils/product-list";

/* Always light: Edit calc / author dialogs stay readable even when OS or
 * app theme is dark (Tailwind dark: follows prefers-color-scheme by default). */
const fieldClass =
  "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm focus:border-[#1463C7] focus:outline-none focus:ring-1 focus:ring-[#1463C7] disabled:cursor-not-allowed disabled:opacity-60";

const btnGhost =
  "inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50";

const btnPrimary =
  "inline-flex items-center gap-1.5 rounded-lg border border-[#1463C7] bg-[#1463C7] px-2.5 py-1.5 text-xs font-semibold text-white shadow-sm hover:border-[#0B3A78] hover:bg-[#0B3A78] disabled:cursor-not-allowed disabled:opacity-50";

const btnIcon =
  "inline-flex h-8 w-8 items-center justify-center rounded-lg border border-transparent text-slate-600 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50";

const checkLabel =
  "inline-flex cursor-pointer items-center gap-1.5 text-xs text-slate-700";

/**
 * Author-time editor for a product list linked to real catalog products.
 * Parent owns persistence via onChange(normalizedList).
 *
 * Supports:
 * - add from register (already linked)
 * - manual draft line → «Knytt til produkt»
 * - per-line / bulk «Oppdater pris fra produkt»
 *
 * Tailwind + PMDialog (no MUI) so Edit calc / module chrome stays on-project.
 */
export default function ProductListEditor({
  value,
  onChange,
  disabled = false,
  compact = false,
  /** When opened from an elevated PMDialog, set above that dialog (e.g. 4300). */
  dialogZIndex = null,
}) {
  const { t } = useTranslation();
  const list = normalizeProductList(value || emptyProductList());
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [linkIndex, setLinkIndex] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshingIndex, setRefreshingIndex] = useState(null);

  const commit = (patch) => {
    onChange?.(normalizeProductList({ ...list, ...patch }));
  };

  const updateOption = (index, patch) => {
    commit({
      options: list.options.map((opt, i) =>
        i === index ? { ...opt, ...patch } : opt
      ),
    });
  };

  const removeOption = (index) => {
    commit({ options: list.options.filter((_, i) => i !== index) });
  };

  const addFromCatalog = () => {
    const existing = new Set(
      list.options
        .filter(isLinkedProductListOption)
        .map((o) => String(o.product_id))
    );
    const added = selectedProducts
      .map((prod, index) =>
        normalizeProductListOption(
          {
            ...prod,
            product_id: prod.id,
            default_selected:
              list.mode === PRODUCT_LIST_MODES.SINGLE
                ? list.options.length === 0 && index === 0
                : true,
          },
          list.options.length + index
        )
      )
      .filter((opt) => opt && !existing.has(String(opt.product_id)));
    commit({ options: [...list.options, ...added] });
    setSelectedProducts([]);
    setSelectorOpen(false);
  };

  const addManualLine = () => {
    const draft = createManualProductListOption(
      {
        displayTitle: t("offer.calculation.product_list.manual_title"),
        default_selected:
          list.mode === PRODUCT_LIST_MODES.SINGLE && list.options.length === 0,
      },
      list.options.length
    );
    commit({ options: [...list.options, draft] });
  };

  const openLinkPicker = (index) => {
    setLinkIndex(index);
    setSelectedProducts([]);
  };

  const closeLinkPicker = () => {
    setLinkIndex(null);
    setSelectedProducts([]);
  };

  const applyLinkSelection = (products) => {
    const product = Array.isArray(products) ? products[0] : products;
    if (linkIndex == null || !product) return;
    const linked = linkProductListOptionToProduct(list.options[linkIndex], product);
    // Avoid duplicate product_id rows when re-linking
    const duplicate = list.options.some(
      (opt, i) =>
        i !== linkIndex &&
        isLinkedProductListOption(opt) &&
        String(opt.product_id) === String(linked.product_id)
    );
    if (duplicate) {
      closeLinkPicker();
      return;
    }
    commit({
      options: list.options.map((opt, i) => (i === linkIndex ? linked : opt)),
    });
    closeLinkPicker();
  };

  const refreshPrices = async () => {
    const linked = list.options.filter(isLinkedProductListOption);
    if (!linked.length) return;
    setRefreshing(true);
    try {
      const pairs = await Promise.all(
        linked.map(async (opt) => {
          try {
            const res = await fetchProduct(opt.product_id);
            const product = res?.data?.product || res?.data || res;
            return [opt.product_id, product];
          } catch {
            return [opt.product_id, null];
          }
        })
      );
      const byId = Object.fromEntries(pairs.filter(([, p]) => p));
      commit(applyCatalogPricesToList(list, byId));
    } finally {
      setRefreshing(false);
    }
  };

  const refreshOnePrice = async (index) => {
    const opt = list.options[index];
    if (!isLinkedProductListOption(opt)) return;
    setRefreshingIndex(index);
    try {
      const res = await fetchProduct(opt.product_id);
      const product = res?.data?.product || res?.data || res;
      if (!product) return;
      updateOption(index, applyCatalogPricesToOption(opt, product));
    } catch {
      // keep snapshot on failure
    } finally {
      setRefreshingIndex(null);
    }
  };

  const linkDialogOpen = linkIndex != null;
  const pickerZIndex = dialogZIndex != null ? dialogZIndex : undefined;

  return (
    <div
      className={
        compact
          ? "product-list-editor product-list-editor--compact space-y-2"
          : "product-list-editor space-y-3 rounded-lg border border-slate-200 bg-slate-50/60 p-3"
      }
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="text-sm font-semibold text-slate-800">
            {t("offer.calculation.product_list.title")}
          </div>
          <p className="text-xs text-slate-500">
            {t("offer.calculation.product_list.hint")}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className={btnGhost}
            disabled={
              disabled ||
              !list.options.some(isLinkedProductListOption) ||
              refreshing
            }
            onClick={refreshPrices}
          >
            <ArrowPathIcon className="h-4 w-4" aria-hidden="true" />
            {t("offer.calculation.product_list.update_prices")}
          </button>
          <button
            type="button"
            className={btnGhost}
            disabled={disabled}
            onClick={addManualLine}
          >
            <PlusIcon className="h-4 w-4" aria-hidden="true" />
            {t("offer.calculation.product_list.add_manual")}
          </button>
          <button
            type="button"
            className={btnPrimary}
            disabled={disabled}
            onClick={() => setSelectorOpen(true)}
          >
            <PlusIcon className="h-4 w-4" aria-hidden="true" />
            {t("offer.calculation.product_list.add_products")}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <label className="grid gap-1 text-xs font-medium text-slate-600">
          <span>{t("offer.calculation.product_list.label")}</span>
          <input
            type="text"
            className={fieldClass}
            value={list.label}
            disabled={disabled}
            onChange={(e) => commit({ label: e.target.value })}
          />
        </label>
        <select
          className={`${fieldClass} self-end`}
          value={list.mode}
          disabled={disabled}
          aria-label={t("offer.calculation.product_list.mode_single")}
          onChange={(e) => commit({ mode: e.target.value })}
        >
          <option value={PRODUCT_LIST_MODES.SINGLE}>
            {t("offer.calculation.product_list.mode_single")}
          </option>
          <option value={PRODUCT_LIST_MODES.MULTI}>
            {t("offer.calculation.product_list.mode_multi")}
          </option>
        </select>
        <label className={`${checkLabel} self-end pb-2`}>
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-slate-300 text-[#1463C7] focus:ring-[#1463C7]"
            checked={list.ask_on_use !== false}
            disabled={disabled}
            onChange={(e) => commit({ ask_on_use: e.target.checked })}
          />
          {t("offer.calculation.product_list.ask_on_use")}
        </label>
      </div>

      {list.options.length === 0 ? (
        <p className="text-xs text-slate-500">
          {t("offer.calculation.product_list.empty")}
        </p>
      ) : (
        <ul className="space-y-2">
          {list.options.map((opt, index) => {
            const linked = isLinkedProductListOption(opt);
            const included = opt.default_selected !== false;
            const linkTitle = linked
              ? t("offer.calculation.product_list.relink_product")
              : t("offer.calculation.product_list.link_product");
            return (
              <li
                key={opt.option_key || `${opt.product_id}-${index}`}
                className={[
                  "flex flex-wrap items-center gap-2 rounded border border-slate-200 bg-white px-2 py-1.5",
                  included ? "" : "opacity-60",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <div className="min-w-0 flex-1 space-y-1">
                  <input
                    type="text"
                    className={fieldClass}
                    disabled={disabled || linked}
                    value={opt.displayTitle}
                    onChange={(e) =>
                      updateOption(index, { displayTitle: e.target.value })
                    }
                    placeholder={t("offer.calculation.product_list.line_title")}
                  />
                  <div className="text-xs text-slate-500">
                    {linked ? (
                      <>
                        #{opt.product_id} ·{" "}
                        {t("offer.calculation.workspace.col.sales_price")}:{" "}
                        {opt.sales_price} ·{" "}
                        {t("offer.calculation.workspace.col.cost_price")}:{" "}
                        {opt.purchase_price}
                        {opt.price_dated_at
                          ? ` · ${t("offer.calculation.product_list.price_dated")}: ${new Date(
                              opt.price_dated_at
                            ).toLocaleString()}`
                          : null}
                      </>
                    ) : (
                      t("offer.calculation.product_list.unlinked")
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-1">
                  <button
                    type="button"
                    className={btnGhost}
                    disabled={disabled}
                    title={linkTitle}
                    onClick={() => openLinkPicker(index)}
                  >
                    <LinkIcon className="h-4 w-4" aria-hidden="true" />
                    {linked
                      ? t("offer.calculation.product_list.relink_product_short")
                      : t("offer.calculation.product_list.link_product")}
                  </button>
                  <button
                    type="button"
                    className={btnIcon}
                    disabled={
                      disabled || !linked || refreshingIndex === index
                    }
                    title={t("offer.calculation.product_list.update_price")}
                    aria-label={t("offer.calculation.product_list.update_price")}
                    onClick={() => refreshOnePrice(index)}
                  >
                    <ArrowPathIcon
                      className={`h-4 w-4 ${
                        refreshingIndex === index ? "animate-spin" : ""
                      }`}
                    />
                  </button>
                  <button
                    type="button"
                    className={btnGhost}
                    disabled={disabled || !linked}
                    onClick={() => {
                      const nextIncluded = !included;
                      if (
                        list.mode === PRODUCT_LIST_MODES.SINGLE &&
                        nextIncluded
                      ) {
                        commit({
                          options: list.options.map((o, i) => ({
                            ...o,
                            default_selected: i === index,
                          })),
                        });
                      } else {
                        updateOption(index, {
                          default_selected: nextIncluded,
                        });
                      }
                    }}
                  >
                    {included
                      ? t("offer.calculation.module.product.exclude")
                      : t("offer.calculation.module.product.include")}
                  </button>
                </div>
                <label className={checkLabel}>
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-[#1463C7] focus:ring-[#1463C7]"
                    checked={Boolean(opt.default_selected)}
                    disabled={disabled || !linked}
                    onChange={(e) => {
                      if (
                        list.mode === PRODUCT_LIST_MODES.SINGLE &&
                        e.target.checked
                      ) {
                        commit({
                          options: list.options.map((o, i) => ({
                            ...o,
                            default_selected: i === index,
                          })),
                        });
                      } else {
                        updateOption(index, {
                          default_selected: e.target.checked,
                        });
                      }
                    }}
                  />
                  {t("offer.calculation.product_list.default_selected")}
                </label>
                <label className={checkLabel}>
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-[#1463C7] focus:ring-[#1463C7]"
                    checked={Boolean(opt.required)}
                    disabled={
                      disabled ||
                      !linked ||
                      list.mode === PRODUCT_LIST_MODES.SINGLE
                    }
                    onChange={(e) =>
                      updateOption(index, { required: e.target.checked })
                    }
                  />
                  {t("offer.calculation.product_list.required")}
                </label>
                <button
                  type="button"
                  className={btnIcon}
                  disabled={disabled}
                  aria-label={t("offer.calculation.product_list.remove")}
                  title={t("offer.calculation.product_list.remove")}
                  onClick={() => removeOption(index)}
                >
                  <TrashIcon className="h-4 w-4 text-red-500" />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <PMDialog
        open={selectorOpen}
        onClose={() => setSelectorOpen(false)}
        size="6xl"
        title="offer.calculation.product_list.add_products"
        closeOnOutsideClick
        dense
        zIndex={pickerZIndex}
        panelClassName="!max-w-6xl lg:!max-w-7xl"
      >
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 overflow-hidden">
            <ModernProductSelectorV2
              embeddedInParentDialog
              multiple
              onChange={setSelectedProducts}
              onClose={() => setSelectorOpen(false)}
              open={selectorOpen}
              selectedProducts={selectedProducts}
              showHeader
              showWarehouseOnlyDefault={false}
            />
          </div>
          <div className="flex flex-shrink-0 justify-end gap-2 border-t border-slate-200 px-1 pt-3">
            <button
              type="button"
              className={btnGhost}
              onClick={() => setSelectorOpen(false)}
            >
              {t("offer.calculation.module.product.cancel")}
            </button>
            <button
              type="button"
              className={btnPrimary}
              disabled={!selectedProducts.length}
              onClick={addFromCatalog}
            >
              {t("offer.calculation.module.product.add_selected")}
            </button>
          </div>
        </div>
      </PMDialog>

      <PMDialog
        open={linkDialogOpen}
        onClose={closeLinkPicker}
        size="6xl"
        title="offer.calculation.product_list.link_product"
        closeOnOutsideClick
        dense
        zIndex={pickerZIndex}
        panelClassName="!max-w-6xl lg:!max-w-7xl"
      >
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 overflow-hidden">
            <ModernProductSelectorV2
              embeddedInParentDialog
              multiple={false}
              onChange={(products) => {
                setSelectedProducts(products);
                if (Array.isArray(products) && products.length === 1) {
                  applyLinkSelection(products);
                }
              }}
              onClose={closeLinkPicker}
              open={linkDialogOpen}
              selectedProducts={selectedProducts}
              showHeader
              showWarehouseOnlyDefault={false}
            />
          </div>
          <div className="flex flex-shrink-0 justify-end gap-2 border-t border-slate-200 px-1 pt-3">
            <button type="button" className={btnGhost} onClick={closeLinkPicker}>
              {t("offer.calculation.module.product.cancel")}
            </button>
            <button
              type="button"
              className={btnPrimary}
              disabled={!selectedProducts.length}
              onClick={() => applyLinkSelection(selectedProducts)}
            >
              {t("offer.calculation.product_list.link_confirm")}
            </button>
          </div>
        </div>
      </PMDialog>
    </div>
  );
}
